import React from 'react';
import { Icon } from '@/components/Icons';
import { SIMULATION_LABELS } from '@/constants';
import type { ISimulationPlanningCard } from '@/types';

interface Props {
  activeCard: ISimulationPlanningCard | null;
}

// ================================
// Static Style Tokens
// ================================
// Centralized class groups keep JSX concise and easier to scan.
const styles = {
  container: 'flex items-start justify-start gap-6 px-6 pb-6 pt-7',
  copyWrap: 'flex min-h-14 flex-col items-start justify-start gap-0.5',
  icon: 'block animate-pulse-fade-in animate-delay-200 animate-duration-300',
  iconWrap: 'flex size-14 items-center justify-center rounded-full bg-accent-surface-red',
  stepRow: 'mb-0 mt-1 flex items-center justify-start text-xs text-neutral-black',
  stepValue: 'block animate-fade-in animate-duration-fast',
  title: `mb-1 block animate-fade-in font-display text-3xl font-bold leading-6 text-neutral-black animate-delay-100 animate-duration-300`,
} as const;

/**
 * ModalHeader
 *
 * Displays the active planning card's icon, step number, and title.
 */
export const ModalHeader: React.FC<Props> = ({ activeCard }) => {
  // ================================
  // Derived View Values
  // ================================
  // Resolve optional card fields once to avoid repeated optional chaining.
  const stepNumber = activeCard?.stepNumber;
  const title = activeCard?.title;
  const iconName = activeCard?.iconName ?? 'CheckCircle';

  // Key changes between steps to replay icon/text entry animations.
  const stepKey = `active-step-${stepNumber ?? 'none'}`;

  // ==================================
  // Render
  // ==================================
  return (
    <div className={styles.container}>
      {/* Icon */}
      <div className={styles.iconWrap}>
        <Icon
          className={styles.icon}
          color="var(--color-brand-primary)"
          key={stepKey}
          name={iconName}
          size={24}
        />
      </div>

      <div className={styles.copyWrap}>
        {/* Step */}
        <p className={styles.stepRow}>
          <span>{SIMULATION_LABELS.STEP}</span>
          <span>&nbsp;</span>
          <span className={styles.stepValue} key={stepKey}>
            {stepNumber}
          </span>
        </p>

        {/* Title */}
        <h3 className={styles.title} key={stepKey}>
          {title}
        </h3>
      </div>
    </div>
  );
};
